import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { DialogService } from 'primeng/dynamicdialog';
import { Skeleton } from 'primeng/skeleton';
import { Menu } from 'primeng/menu';
import { MenuItem } from 'primeng/api';
import { CarePlanStore } from '../../care-plan.store';
import { CaseListComponent } from '../../cases/case-list/case-list.component';
import {
  OpenCaseDialogComponent,
  OpenCaseDialogData,
  OpenCaseDialogResult,
} from '../../cases/open-case-dialog/open-case-dialog.component';
import {
  CARE_PLAN_DIALOG_OPTIONS,
  OPEN_CASE_DIALOG_OPTIONS,
} from '../../care-plan-dialog.options';
import { GoalsBarComponent } from '../../goals/goals-bar/goals-bar.component';
import { ManageGoalsDialogComponent } from '../../goals/manage-goals-dialog/manage-goals-dialog.component';

/**
 * Top-level Care Plan tab body. Provides `CarePlanStore` + `DialogService`
 * scoped to this component tree (mirrors `FinancialSnapshotStore`'s
 * scoping) — no root singleton. The authenticated Case API client is
 * application-provided while client-specific state remains panel-local.
 */
@Component({
  selector: 'bn-care-plan-panel',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [CarePlanStore, DialogService],
  imports: [CaseListComponent, Skeleton, GoalsBarComponent, Menu],
  templateUrl: './care-plan-panel.component.html',
  styleUrl: './care-plan-panel.component.scss',
})
export class CarePlanPanelComponent {
  protected readonly store = inject(CarePlanStore);
  private readonly _dialog = inject(DialogService);
  protected readonly caseList = viewChild(CaseListComponent);

  /** Care Plan doesn't own client-list routing — the host decides what this does. */
  readonly backToClientList = output<void>();

  protected readonly addActionDisabledReason = computed(() => {
    if (this.store.isLive() && !this.store.canEditLiveCases()) {
      return 'You have view-only Care Plan access.';
    }
    if (this.store.loading()) return 'Loading Care Plan.';
    if (this.store.error()) return 'Care Plan could not be loaded.';
    const list = this.caseList();
    return list ? list.addActionDisabledReason() : 'Loading Care Plan.';
  });

  protected readonly openCaseDisabledReason = computed(() => {
    if (!this.store.isLive()) return null;
    if (!this.store.canEditLiveCases()) {
      return 'You have view-only Care Plan access.';
    }
    if (!this.store.canCreateLiveCase()) {
      return 'Select a client before opening a case.';
    }
    if (this.store.domainError()) {
      return 'AIM domain reference data could not be loaded.';
    }
    if (this.store.domainLoading() || !this.store.domainsLoaded()) {
      return 'Loading AIM domain reference data.';
    }
    if (this.store.domains().length === 0) {
      return 'No AIM case domains are available.';
    }
    return null;
  });

  protected readonly addActionLoading = computed(
    () =>
      this.store.loading() ||
      !this.caseList() ||
      !!this.caseList()?.actionsLoading(),
  );
  protected readonly addActionNotice = computed(() => {
    if (
      this.store.isLive() &&
      this.store.canEditLiveCases() &&
      !this.store.canCreateLiveCase()
    )
      return null;
    return this.addActionLoading() ? null : this.addActionDisabledReason();
  });
  protected readonly openCaseNotice = computed(() => {
    if (
      this.store.isLive() &&
      this.store.canEditLiveCases() &&
      !this.store.canCreateLiveCase()
    )
      return null;
    if (
      this.store.isLive() &&
      this.store.canEditLiveCases() &&
      this.store.canCreateLiveCase() &&
      !this.store.domainError() &&
      (this.store.domainLoading() || !this.store.domainsLoaded())
    )
      return null;
    return this.openCaseDisabledReason();
  });

  protected readonly actionMenuOpen = signal(false);
  protected readonly actionMenuItems = computed<MenuItem[]>(() => [
    {
      label: 'Add action',
      icon: 'pi pi-plus',
      disabled: this.addActionLoading() || !!this.addActionDisabledReason(),
      command: () => this.caseList()?.openAddAction(),
    },
    {
      label: 'Open a case',
      icon: 'pi pi-plus',
      disabled: !!this.openCaseDisabledReason(),
      command: () => this.openCaseDialog(),
    },
  ]);

  openManageGoals(): void {
    if (!this.store.canManageGoals()) return;
    this._dialog.open(ManageGoalsDialogComponent, {
      ...CARE_PLAN_DIALOG_OPTIONS,
      styleClass: 'bn-manage-goals-dialog-host',
      data: { store: this.store },
    });
  }

  retryGoals(): void {
    void this.store.refreshGoals();
  }

  openCaseDialog(): void {
    if (this.openCaseDisabledReason()) return;
    const ref = this._dialog.open(OpenCaseDialogComponent, {
      ...OPEN_CASE_DIALOG_OPTIONS,
      styleClass: 'bn-open-case-dialog-host',
      data: { store: this.store } satisfies OpenCaseDialogData,
    });
    ref?.onClose.subscribe((result?: OpenCaseDialogResult) => {
      const caseId = result?.createdCaseId ?? result?.navigateToCaseId;
      if (caseId) {
        this.openCaseDetail(caseId);
      }
    });
  }

  /** Case detail lives inline in the list (D11), so "open" means expand its row. */
  openCaseDetail(caseId: string): void {
    // A case selected from duplicate detection or just created must be made
    // visible even when the list was previously narrowed by any filter.
    this.store.resetFilters();
    this.store.setExpandedCase(caseId);
  }
}
